import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { computed } from "mobx"
import { observer, useLocalObservable } from "mobx-react"
import { Button, Flex, Modal, Popconfirm } from "antd"

import { NoContent } from "@src/app/components/NoContent/Index"
import { notify } from "@src/app/components/notify"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { MassPayoutPageVariants } from "@src/pages/massPayouts/constants"
import { PayoutsCardCollection } from "@src/pages/payouts/classes/PayoutsCardCollection"
import { PayoutsCryptoCollection } from "@src/pages/payouts/classes/PayoutsCryptoCollection"
import { PayoutsCardTable } from "@src/pages/payouts/ui/PayoutsCardTable"
import { PayoutsCryptoTable } from "@src/pages/payouts/ui/PayoutsCryptoTable"
import { PayoutsConformationProcess } from "@src/pages/payoutsConfirmation/classes/PayoutsConformationProcess"
import { FormOTP } from "@src/shared/componemts/FormOTP"

type Props = {
  view: MassPayoutPageVariants
}

export const PayoutsPage = observer(({ view }: Props) => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [isOtpOpen, setIsOtpOpen] = useState(false)
  const [isActing, setIsActing] = useState(false)
  const [isProcessed, setIsProcessed] = useState(false)
  const process = useLocalObservable(() => new PayoutsConformationProcess())

  useEffect(() => {
    process.setState({
      id: id!,
      payouts: [],
      type: view === MassPayoutPageVariants.Crypto ? "crypto" : "card",
    })

    setIsProcessed(false)
    setIsOtpOpen(false)
  }, [id, view, process])

  const payouts = useMemo(
    () =>
      view === MassPayoutPageVariants.Crypto
        ? new PayoutsCryptoCollection(id!)
        : new PayoutsCardCollection(id!),
    [id, view],
  )

  const canProcess =
    !isProcessed && payouts.items.some(({ status }) => status === "new")

  const refresh = () => payouts.load(payouts.state.prevRequest ?? { id })

  const handleConfirm = async (otp: string) => {
    setIsActing(true)
    try {
      const confirmed = await process.confirm(otp)
      if (!confirmed) throw new Error("Payout confirmation failed")

      setIsProcessed(true)
      notify.success("Funds have been sent")
      await refresh()
    } finally {
      setIsActing(false)
    }
  }

  const handleCancel = async () => {
    setIsActing(true)
    try {
      const status = await process.cancel()

      if (status) {
        setIsProcessed(true)
        notify.success("Payouts have been cancelled")
        navigate(-1)
      }
    } finally {
      setIsActing(false)
    }
  }

  const [isEmpty, isLoading] = computed(() => {
    if (payouts.filter.isActive) {
      return [false, false]
    }

    return [payouts.isEmpty, payouts.isLoading && payouts.isEmpty]
  }).get()

  const table = useMemo(() => {
    if (view === MassPayoutPageVariants.Crypto) {
      return (
        <PayoutsCryptoTable collection={payouts as PayoutsCryptoCollection} />
      )
    }

    return <PayoutsCardTable collection={payouts as PayoutsCardCollection} />
  }, [view, payouts])

  return (
    <DefaultPageLayout
      emptyBlock={<NoContent title="No available payouts" />}
      isEmpty={isEmpty}
      isLoading={isLoading}
      title={`Payouts ${id}`}
    >
      <Flex gap={24} vertical>
        {table}
        {canProcess && (
          <Flex gap={16} justify="center">
            <Popconfirm
              description="This cancels the entire mass payout."
              disabled={isActing}
              onConfirm={handleCancel}
              title="Cancel this mass payout?"
            >
              <Button disabled={isActing} size="large">
                Cancel
              </Button>
            </Popconfirm>
            <Button
              disabled={isActing}
              onClick={() => setIsOtpOpen(true)}
              size="large"
              type="primary"
            >
              Send funds
            </Button>
          </Flex>
        )}
        <Modal
          closable={!isActing}
          destroyOnClose
          footer={null}
          maskClosable={!isActing}
          onCancel={() => setIsOtpOpen(false)}
          open={isOtpOpen}
          title="2FA check"
        >
          <FormOTP
            isDisabled={isActing}
            onSubmit={handleConfirm}
            setVisible={setIsOtpOpen}
          />
        </Modal>
      </Flex>
    </DefaultPageLayout>
  )
})
